import React, { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { state } from '../store/global';

import TransferHeader from '../components/common/TransferHeader';
import PaymentRoute from '../components/common/PaymentRoute';
import NetworkItem from '../components/common/NetworkItem';
import TransferForm from '../components/common/TransferForm';

import ColombiaFlag from '../assets/images/country/colombia.png';
import MexicoFlag from '../assets/images/country/mexico.png';
import UsFlag from '../assets/images/country/us.png';
import CanadaFlag from '../assets/images/country/canada.png';
import EuropeFlag from '../assets/images/country/europe.png';
import AsiaFlag from '../assets/images/country/asia.png';
import ChinaFlag from '../assets/images/country/china.png';
import KoreaFlag from '../assets/images/country/korea.png';
import PhilippineFlag from '../assets/images/country/philippine.png';
import IndiaFlag from '../assets/images/country/india.png';
import IndonesiaFlag from '../assets/images/country/indonesia.png';
import MalasiaFlag from '../assets/images/country/malasia.png';
import SingaporeFlag from '../assets/images/country/singapore.png';
import ThailandFlag from '../assets/images/country/thailand.png';
import VietnamFlag from '../assets/images/country/vietnam.png';
import BrazilFlag from '../assets/images/country/brazil.png';

export default function Transfer() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  // null = show transfer type selector, 'international' = country selection, 'user' = redirect to SendToUser
  const [transferType, setTransferType] = useState(null);

  const [country, setCountry] = useState({
    icon: '',
    name: '',
    paymentRoutes: [],
    currency: '',
    isoCode3: '',
  });
  const [paymentRoute, setPaymentRoute] = useState('');
  const [isAsia, setIsAsia] = useState(false);
  const countries = [
    {
      icon: ColombiaFlag,
      name: 'Colombia',
      paymentRoutes: [],
      currency: 'COP',
      isoCode3: 'COL',
    },
    {
      icon: MexicoFlag,
      name: 'México',
      paymentRoutes: ['SPEI'],
      currency: 'MXN',
      isoCode3: 'MEX',
    },
    {
      icon: UsFlag,
      name: 'Estados Unidos',
      paymentRoutes: ['ACH', 'WIRE'],
      currency: 'USD',
      isoCode3: 'USA',
    },
    {
      icon: CanadaFlag,
      name: 'Canadá',
      paymentRoutes: [],
      currency: 'CAD',
      isoCode3: 'CAN',
      disabled: true,
    },
    {
      icon: EuropeFlag,
      name: 'Europa',
      paymentRoutes: ['SEPA'],
      currency: 'EUR',
      isoCode3: 'EUR', // Represents the Eurozone
    },
    {
      icon: BrazilFlag,
      name: 'Brasil',
      paymentRoutes: ['PIX'],
      currency: 'BRL',
      isoCode3: 'BRA',
    },
    {
      icon: AsiaFlag,
      name: 'Asia',
    },
    {
      icon: ChinaFlag,
      name: 'China',
      paymentRoutes: ['BIC/SWIFT', 'CNAPS'],
      currency: 'CNY',
      isoCode3: 'CHN',
    },
    {
      icon: KoreaFlag,
      name: 'Corea del sur',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'KRW',
      isoCode3: 'KOR',
    },
    {
      icon: PhilippineFlag,
      name: 'Filipinas',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'PHP',
      isoCode3: 'PHL',
    },
    {
      icon: IndiaFlag,
      name: 'India',
      paymentRoutes: [],
      currency: 'INR',
      isoCode3: 'IND',
      disabled: true,
    },
    {
      icon: IndonesiaFlag,
      name: 'Indonesia',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'IDR',
      isoCode3: 'IDN',
    },
    {
      icon: MalasiaFlag,
      name: 'Malasia',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'MYR',
      isoCode3: 'MYS',
    },
    {
      icon: SingaporeFlag,
      name: 'Singapour',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'SGD',
      isoCode3: 'SGP',
    },
    {
      icon: ThailandFlag,
      name: 'Tailandia',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'THB',
      isoCode3: 'THA',
    },
    {
      icon: VietnamFlag,
      name: 'Vietnam',
      paymentRoutes: ['BIC/SWIFT', 'Código de banco'],
      currency: 'VND',
      isoCode3: 'VNM',
    },
  ];
  const countryData = isAsia ? countries.slice(7, 16) : countries.slice(0, 7);
  const onChangeCountry = (item) => {
    if (item.name == 'Asia') setIsAsia(true);
    else setCountry(item);
  };
  const paymentRouteDescription = (route) => {
    if (route == 'ACH')
      return 'Tarda entre y 1-2 días hábiles, costo X por transferencia.';
    else if (route == 'WIRE')
      return 'Transferencia en el mismo día, costo X por transferencia';
    else if (route == 'BIC/SWIFT') return 'Para transferencias internacionales';
    else if (route == 'SEPA')
      return `Solo para transferencias dentro de la región ${country.name}`;
    else if (route == 'PIX')
      return 'Transferencia instantánea via PIX a Brasil';
    else return `Solo para transferencias locales dentro de ${country.name}.`;
  };
  const goBack = () => {
    setCountry({ icon: '', name: '', paymentRoutes: [], currency: '', isoCode3: '' });
    setPaymentRoute('');
  };

  const goBackToSelector = () => {
    setTransferType(null);
    setCountry({ icon: '', name: '', paymentRoutes: [], currency: '', isoCode3: '' });
    setPaymentRoute('');
    setIsAsia(false);
  };

  // Transfer type selector (hub)
  if (!transferType) {
    return (
      <div className="relative py-8 flex items-center justify-center">
        <div className="w-153.5 bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-6">
          <TransferHeader
            headerTitle="Transferir"
            description="Selecciona el tipo de transferencia que deseas realizar."
            goBack={() => navigate('/dashboard')}
          />

          <div className="space-y-3">
            {/* International transfer option */}
            <button
              onClick={() => setTransferType('international')}
              className="w-full px-6 py-4 cursor-pointer bg-[#FCFCFC] border border-[#CDD9F0] rounded-[14px] flex items-center justify-between hover:bg-[#F0F4FF] transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-[#E6EBF7] rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#2A59C6" strokeWidth="1.5"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#2A59C6" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-[#181D27] font-[poppins]">A cuenta bancaria internacional</p>
                  <p className="text-xs text-[#535862] font-[poppins]">Envia a bancos en Mexico, USA, Europa, Brasil, Asia y mas</p>
                </div>
              </div>
              <svg className="w-5 h-5 text-[#A4A7AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* FlowexPay user transfer option */}
            <button
              onClick={() => navigate('/dashboard/send')}
              className="w-full px-6 py-4 cursor-pointer bg-[#FCFCFC] border border-[#CDD9F0] rounded-[14px] flex items-center justify-between hover:bg-[#F0F4FF] transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-[#E6EBF7] rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="#2A59C6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="9" cy="7" r="4" stroke="#2A59C6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#2A59C6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-[#181D27] font-[poppins]">A usuario FlowexPay</p>
                  <p className="text-xs text-[#535862] font-[poppins]">Envio inmediato y sin costo entre usuarios FlowexPay</p>
                </div>
              </div>
              <svg className="w-5 h-5 text-[#A4A7AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (country.name) {
    if (
      !country.paymentRoutes.length ||
      (country.paymentRoutes.length && paymentRoute)
    ) {
      return (
        <TransferForm
          country={country}
          paymentRoute={paymentRoute}
          goBack={goBack}
        />
      );
    }

    return (
      <div className="relative py-8 flex items-center justify-center">
        <div className="w-153.5 bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-6">
          <TransferHeader
            headerTitle={`Transferir a cuenta en ${country.name}`}
            description="Selecciona el método de transferencia"
            goBack={goBack}
          />

          <div className="space-y-2">
            {country.paymentRoutes.map((route, idx) => (
              <PaymentRoute
                key={idx}
                route={route}
                description={paymentRouteDescription(route)}
                onClick={() => setPaymentRoute(route)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative py-8 flex items-center justify-center">
      <div className="w-153.5 bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-6">
        <TransferHeader
          headerTitle={`Transferir${isAsia ? ' a Asia' : ''}`}
          description={
            isAsia
              ? 'Selecciona el país de destino'
              : 'Selecciona la región de destino'
          }
          goBack={() => {
            if (isAsia) setIsAsia(false);
            else goBackToSelector();
          }}
        />

        <div className="space-y-2 pb-6">
          {countryData.map((item, idx) => (
            <NetworkItem
              key={idx}
              item={item}
              onClick={() => onChangeCountry(item)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
